// Tela de Informativos (/announcements) — mockup "Informativos Lista":
// header 60px com subtítulo e selo Super admin, chips de filtro alimentados
// por `totals` do backend, e card de tabela em CSS grid.
// Estilos compartilhados em ../Campaigns/campaignPageStyles.
import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CampaignIcon from "@material-ui/icons/RecordVoiceOver";
import ImageIcon from "@material-ui/icons/Image";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import MainContainer from "../../components/MainContainer";
import AnnouncementModal from "../../components/AnnouncementModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  priorityMeta,
  chipsWithTotals,
  chipQuery,
  excerpt,
} from "./announcementRules";

const TABLE_GRID = "minmax(0,2.6fr) 130px minmax(0,1.3fr) minmax(0,1.1fr) 110px 100px";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    announcements.forEach((announcement) => {
      const announcementIndex = state.findIndex((u) => u.id === announcement.id);
      if (announcementIndex !== -1) {
        state[announcementIndex] = announcement;
      } else {
        newAnnouncements.push(announcement);
      }
    });

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useLocalStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    headerTitleRow: { display: "flex", alignItems: "center", gap: 8 },
    superBadge: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.12),
      borderRadius: 6,
      padding: "2px 7px",
    },
    headerSubtitle: { fontSize: 12, color: theme.palette.text.secondary },
    chips: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 18 },
    chip: {
      borderRadius: 99,
      padding: "5px 14px",
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      display: "flex",
      gap: 6,
      alignItems: "center",
      color: theme.palette.text.secondary,
      background: "none",
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      "&:hover": { color: theme.palette.text.primary },
    },
    chipOn: {
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: alpha("#A044FF", 0.15),
      borderColor: "transparent",
    },
    chipCount: { fontSize: 10.5, opacity: 0.7, fontVariantNumeric: "tabular-nums" },
    titleTile: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    excerpt: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    mediaCell: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      minWidth: 0,
      "& .MuiSvgIcon-root": {
        fontSize: 15,
        color: stateText(theme, AEROS.cyan, "info"),
        flexShrink: 0,
      },
    },
    noMedia: { color: theme.palette.aeros.textTertiary },
    footNote: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 18,
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
  };
});

const Announcements = () => {
  const c = useCampaignPageStyles({ grid: TABLE_GRID });
  const s = useLocalStyles();
  const theme = useTheme();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [activeChip, setActiveChip] = useState("all");
  const [totals, setTotals] = useState({});
  const [announcements, dispatch] = useReducer(reducer, []);

  // trava para nao acessar pagina que não pode
  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error(i18n.t("announcements.toasts.forbidden"));
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, activeChip]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber, activeChip]);

  useEffect(() => {
    if (user.companyId && socket) {
      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
        // O socket mexe nas linhas, mas os totais dos chips vêm do backend —
        // sem isto eles ficam parados em "Todos 1" depois de excluir.
        refreshTotals();
      };

      socket.on(`company-announcement`, onCompanyAnnouncement);
      return () => {
        socket.off(`company-announcement`, onCompanyAnnouncement);
      };
    }
    return undefined;
  }, [user]);

  // Só os totais dos chips, sem mexer nas linhas já carregadas.
  const refreshTotals = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber: 1, ...chipQuery(activeChip) },
      });
      if (data.totals) setTotals(data.totals);
    } catch (err) {
      // silencioso: é só a contagem dos chips
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber, ...chipQuery(activeChip) },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      // `totals` conta o conjunto todo, não a página — os chips não se
      // zeram entre si nem mudam conforme o scroll carrega mais linhas.
      if (data.totals) setTotals(data.totals);
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
        await api.delete(`/announcements/${announcement.id}/media-upload`);

      await api.delete(`/announcements/${announcement.id}`);

      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    // `setSearchParam("")`/`setPageNumber(1)` não disparavam refetch quando já
    // estavam nesses valores — os totais ficavam velhos. Atualiza direto.
    setSearchParam("");
    setPageNumber(1);
    refreshTotals();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const stop = (event, fn) => {
    event.stopPropagation();
    fn();
  };

  const chips = chipsWithTotals(totals, activeChip);

  return (
    <MainContainer>
      <AerosConfirmModal
        open={Boolean(deletingAnnouncement)}
        onClose={() => setDeletingAnnouncement(null)}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("announcements.confirmDelete.title", {
          name: deletingAnnouncement?.title || "",
        })}
        description={i18n.t("announcements.confirmDelete.text")}
        confirmLabel={i18n.t("announcements.confirmDelete.confirm")}
      />
      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />

      <div className={c.pageHeader}>
        <div>
          <Typography component="div" className={s.headerTitleRow}>
            <span className={c.pageTitle}>{i18n.t("announcements.title")}</span>
            <span className={c.pageTitleCount}>· {totals.all ?? announcements.length}</span>
            <span className={s.superBadge}>{i18n.t("announcements.superAdmin")}</span>
          </Typography>
          <div className={s.headerSubtitle}>{i18n.t("announcements.subtitle")}</div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("announcements.search")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("announcements.search") }}
          />
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={handleOpenAnnouncementModal}
          >
            {i18n.t("announcements.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody} onScroll={handleScroll}>
        <div className={s.chips}>
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className={`${s.chip} ${chip.active ? s.chipOn : ""}`}
              aria-pressed={chip.active}
              onClick={() => setActiveChip(chip.id)}
            >
              {chip.label}
              {chip.count !== null && (
                <span className={s.chipCount}>{chip.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className={c.tableCard}>
          <div className={c.tableHeadRow}>
            <div className={c.tableHeadCell} style={{ paddingLeft: 20 }}>
              {i18n.t("announcements.table.title")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("announcements.table.priority")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("announcements.table.media")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("announcements.table.published")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("announcements.table.status")}
            </div>
            <div className={`${c.tableHeadCell} ${c.cellActionsHead}`}>
              {i18n.t("announcements.table.actions")}
            </div>
          </div>

          {announcements.map((announcement) => {
            const pr = priorityMeta(announcement.priority);
            return (
              <div
                key={announcement.id}
                className={`${c.tableRow} ${c.tableRowClickable}`}
                onClick={() => handleEditAnnouncement(announcement)}
              >
                <div className={c.nameCell}>
                  <div
                    className={s.titleTile}
                    style={{
                      backgroundColor: alpha(pr.color, 0.12),
                      color: stateText(theme, pr.color, pr.stateKey),
                    }}
                  >
                    <CampaignIcon />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className={c.nameText}>{announcement.title}</div>
                    <div className={s.excerpt}>{excerpt(announcement.text)}</div>
                  </div>
                </div>

                <div className={c.tableCell}>
                  <span className={`${c.statusPill} ${c[pr.tone]}`}>
                    <span className={c.statusDot} />
                    {pr.label}
                  </span>
                </div>

                <div className={c.tableCell}>
                  {announcement.mediaName ? (
                    <span className={s.mediaCell} title={announcement.mediaName}>
                      <ImageIcon />
                      <span className={c.cellEllipsis}>{announcement.mediaName}</span>
                    </span>
                  ) : (
                    <span className={`${s.mediaCell} ${s.noMedia}`}>
                      {i18n.t("announcements.noMedia")}
                    </span>
                  )}
                </div>

                <div className={`${c.tableCell} ${c.cellMuted}`}>
                  {datetimeToClient(announcement.createdAt)}
                </div>

                <div className={c.tableCell}>
                  <span
                    className={`${c.statusPill} ${
                      announcement.status ? c.toneSuccess : c.toneNeutral
                    }`}
                  >
                    {announcement.status
                      ? i18n.t("announcements.active")
                      : i18n.t("announcements.inactive")}
                  </span>
                </div>

                <div className={`${c.tableCell} ${c.cellActions}`}>
                  <Tooltip title={i18n.t("announcements.actions.edit")}>
                    <IconButton
                      className={c.rowActionButton}
                      aria-label={i18n.t("announcements.actions.edit")}
                      onClick={(e) => stop(e, () => handleEditAnnouncement(announcement))}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("announcements.actions.delete")}>
                    <IconButton
                      className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                      aria-label={i18n.t("announcements.actions.delete")}
                      onClick={(e) => stop(e, () => setDeletingAnnouncement(announcement))}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            );
          })}

          {loading && (
            <div className={c.loadingRow}>
              <CircularProgress size={22} />
            </div>
          )}
          {!loading && announcements.length === 0 && (
            <div className={c.emptyState}>
              <div className={c.emptyIcon}>
                <CampaignIcon />
              </div>
              <div className={c.emptyTitle}>
                {searchParam || activeChip !== "all"
                  ? i18n.t("announcements.empty.noResults")
                  : i18n.t("announcements.empty.title")}
              </div>
              {!searchParam && activeChip === "all" && (
                <div className={c.emptyText}>{i18n.t("announcements.empty.text")}</div>
              )}
            </div>
          )}
        </div>

        <div className={s.footNote}>
          <InfoOutlinedIcon />
          {i18n.t("announcements.footNote")}
        </div>
      </div>
    </MainContainer>
  );
};

export default Announcements;
